import { ReactNode, Suspense, lazy, useEffect, useState } from 'react';

import { CarouselApi } from '@componentLibrary/Carousel/carousel';
import ImageCarousel from '@componentLibrary/ImageCarousel';
import Typography from '@componentLibrary/Typography';
import { useMediaQuery } from '@mui/material';

import FullscreenSplitModal from '@components/fullscreen-split-modal/fullscreen-split-modal';

const MwlAdminLottieGraphic = lazy(async () => {
    const [{ default: LottieGraphic }, { default: mwlAdminJSON }] = await Promise.all([
        import('@components/graphics/LottieGraphic'),
        import('@components/graphics/lottie/mwl_admin.json'),
    ]);

    return {
        default: function MwlAdminLottieGraphic() {
            return <LottieGraphic json={mwlAdminJSON} startPlay renderer="svg" />;
        },
    };
});

type LoginPromoContent = {
    key: string;
    text: string;
    delay?: number;
} & (
    | {
          image: string;
          lottie?: never;
      }
    | {
          lottie: 'mwl-admin';
          image?: never;
      }
);

const LoginPromo = ({
    loginForm,
    isEmployee = false,
}: {
    loginForm: ReactNode;
    isEmployee?: boolean;
}) => {
    const [carouselApi, setCarouselApi] = useState<CarouselApi>();
    const [current, setCurrent] = useState(1);
    const [lottiePlayKeys, setLottiePlayKeys] = useState<Record<string, number>>({});
    const hidePromo = useMediaQuery(`(max-width:1280px)`);

    useEffect(() => {
        if (!carouselApi) {
            return;
        }

        setCurrent(carouselApi.selectedScrollSnap() + 1);

        carouselApi.on('select', () => {
            setCurrent(carouselApi.selectedScrollSnap() + 1);
        });
    }, [carouselApi]);

    const content: LoginPromoContent[] = isEmployee
        ? [
              {
                  key: 'emp-skills',
                  text: 'Get paid to build your skills on the floor.',
                  image: '/static/images/login-promo/Graphic_EMP01.png',
              },
              {
                  key: 'emp-lessons',
                  text: 'Explore product tips and quick lessons while on shift.',
                  image: '/static/images/login-promo/Graphic_EMP02.png',
              },
              {
                  key: 'emp-events',
                  text: 'See what Events are happening in your store today.',
                  image: '/static/images/login-promo/Graphic_EMP03.png',
              },
          ]
        : [
              {
                  key: 'adm-competitions',
                  text: 'State-wide competitions across your market.',
                  lottie: 'mwl-admin',
                  delay: 8000,
              },
              {
                  key: 'adm-courses',
                  text: 'Empower employees to succeed with Courses.',
                  image: '/static/images/login-promo/Graphic_ADM01.png',
              },
              {
                  key: 'adm-calendar',
                  text: 'View all of your events on a single calendar.',
                  image: '/static/images/login-promo/Graphic_ADM03.png',
              },
              {
                  key: 'adm-metrics',
                  text: 'Analyze Spark effectiveness with new metrics.',
                  image: '/static/images/login-promo/Graphic_ADM02.png',
              },
          ];

    // make sure the animation resets between each play
    useEffect(() => {
        if (!carouselApi) return;

        const currentItem = content[carouselApi.selectedScrollSnap()];
        if (currentItem?.lottie) {
            setLottiePlayKeys((prev) => ({
                ...prev,
                [currentItem.key]: (prev[currentItem.key] ?? 0) + 1,
            }));
        }
    }, [carouselApi, current]);

    useEffect(() => {
        if (!carouselApi) {
            return undefined;
        }

        const currentIndex = carouselApi.selectedScrollSnap();
        const delay = content[currentIndex]?.delay || 5000;

        const timer = setTimeout(() => {
            carouselApi.scrollNext();
        }, delay);

        return () => clearTimeout(timer);
    }, [carouselApi, current]);

    return (
        <>
            {hidePromo ? (
                <div className="flex w-full justify-center">{loginForm}</div>
            ) : (
                <FullscreenSplitModal
                    isVisible
                    left={loginForm}
                    right={
                        <div className="flex flex-col gap-3 p-4 h-full justify-center bg-cerulean-600 text-center">
                            <div className="flex justify-center w-full">
                                <Typography
                                    variant="sm"
                                    className="text-gray-950 font-semibold py-1 px-2 bg-white rounded w-fit"
                                >
                                    Latest updates
                                </Typography>
                            </div>
                            <Typography variant="xl" className="font-bold text-white">
                                {content[current - 1].text}
                            </Typography>
                            <ImageCarousel
                                items={content.map((item) =>
                                    item.image ? (
                                        <img
                                            src={item.image}
                                            alt=""
                                            style={{ width: '100%' }}
                                            key={item.key}
                                        />
                                    ) : (
                                        <Suspense
                                            fallback={null}
                                            key={`${item.key}-${lottiePlayKeys[item.key] ?? 0}`}
                                        >
                                            <MwlAdminLottieGraphic />
                                        </Suspense>
                                    ),
                                )}
                                setCarouselApi={setCarouselApi}
                                showButtons={false}
                                className="h-fit"
                                carouselClassName="w-full h-fit"
                                navigationDotsClassName="-bottom-4"
                                opts={{
                                    loop: true,
                                }}
                            />
                        </div>
                    }
                    onClose={() => {}}
                    showHeader={false}
                    showCloseButton={false}
                    className="flex !z-[8]"
                />
            )}
        </>
    );
};

export default LoginPromo;
